// client/src/components/forms/CascadingScopePicker.js
//
// The audience picker for coach-side forms. Composes a segmented
// control (Whole org / Age group / Team) with dependent selects that
// filter each other:
//
//   - Whole org → no additional selects
//   - Age group → Age group select
//   - Team      → Age group select + Team select (filtered by age group)
//
// Emits { scope, age_group_id?, team_id? } — the exact shape POST
// /announcements consumes, so callsites can submit it as-is.
//
// Cross-field behavior (per §3b):
//   - Picking a team pre-fills age_group_id if the caller hasn't
//     picked one yet.
//   - Changing age_group_id clears team_id if that team no longer
//     belongs to the new age group.
//   - Downgrading scope (team → age_group → org) drops the newly-
//     irrelevant fields on the way down.
//
// Self-fetches /age-groups and /teams so future callsites (Practice,
// Schedule) don't duplicate the fetch. useFetch unwraps { success,
// data } → data (verified in OrgProvider's /me usage).

import { useFetch } from '../../hooks/useFetch';
import { FormField } from './FormField';
import { Select } from './Select';

const SCOPE_OPTIONS = [
  { value: 'org',       label: 'Whole org' },
  { value: 'age_group', label: 'Age group' },
  { value: 'team',      label: 'Team' },
];

export function CascadingScopePicker({ value, onChange }) {
  const { data: ageGroups, loading: agLoading, error: agError } = useFetch('/age-groups');
  const { data: teams,     loading: tLoading,  error: tError  } = useFetch('/teams');

  const setScope = (nextScope) => {
    // Downgrading drops irrelevant fields. Upgrading preserves what's
    // already picked so the coach doesn't have to re-choose after
    // toggling.
    if (nextScope === 'org') {
      onChange({ scope: 'org' });
    } else if (nextScope === 'age_group') {
      onChange({ scope: 'age_group', age_group_id: value.age_group_id });
    } else {
      onChange({
        scope: 'team',
        age_group_id: value.age_group_id,
        team_id: value.team_id,
      });
    }
  };

  const setAgeGroup = (age_group_id) => {
    // If a team was picked and doesn't belong to the new age group,
    // clear it. String() on both sides because ROWIDs come back as
    // bigint strings from ZCQL but the Select's value is a plain
    // string — coerce both, compare safely.
    const teamStillValid =
      value.team_id &&
      teams?.find((t) => String(t.id) === String(value.team_id))?.age_group_id ===
        age_group_id;
    onChange({
      ...value,
      age_group_id: age_group_id || undefined,
      team_id: teamStillValid ? value.team_id : undefined,
    });
  };

  const setTeam = (team_id) => {
    // Pre-fill age_group_id from the team if the caller hasn't picked
    // one yet. If they have, respect their pick.
    const team = teams?.find((t) => String(t.id) === String(team_id));
    const inferredAgeGroup = value.age_group_id || team?.age_group_id;
    onChange({
      ...value,
      team_id: team_id || undefined,
      age_group_id: inferredAgeGroup,
    });
  };

  // Teams filtered to the picked age group. Empty until an age group
  // is picked, at which point the Team select becomes populated.
  const teamsInAgeGroup =
    value.age_group_id && teams
      ? teams.filter((t) => String(t.age_group_id) === String(value.age_group_id))
      : [];

  // Both endpoints are cheap and typically resolve in <200ms. Render
  // the segmented control immediately (no data needed) and show a
  // small note where the selects would be while loading. Error state
  // replaces the selects but keeps the segmented control interactive.
  const loading = agLoading || tLoading;
  const error = agError || tError;

  return (
    <div className="space-y-4">
      <FormField label="Audience">
        <SegmentedControl
          value={value.scope}
          onChange={setScope}
          options={SCOPE_OPTIONS}
        />
      </FormField>

      {(value.scope === 'age_group' || value.scope === 'team') && (
        <>
          {loading && (
            <p className="text-inkSub text-sm">Loading options…</p>
          )}
          {error && (
            <p className="text-red text-sm">Couldn't load audience options.</p>
          )}
          {!loading && !error && (
            <FormField label="Age group">
              <Select
                value={value.age_group_id ?? ''}
                onChange={(v) => setAgeGroup(v || null)}
              >
                <option value="">Pick an age group…</option>
                {ageGroups.map((ag) => (
                  <option key={ag.id} value={ag.id}>
                    {ag.name}
                  </option>
                ))}
              </Select>
            </FormField>
          )}

          {value.scope === 'team' && !loading && !error && (
            <FormField
              label="Team"
              helperText={
                !value.age_group_id ? 'Pick an age group first' : undefined
              }
            >
              <Select
                value={value.team_id ?? ''}
                onChange={(v) => setTeam(v || null)}
                disabled={!value.age_group_id}
              >
                <option value="">
                  {value.age_group_id
                    ? 'Pick a team…'
                    : 'Waiting on age group…'}
                </option>
                {teamsInAgeGroup.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
        </>
      )}
    </div>
  );
}

// Internal — pill-shaped segmented control. Modeled after the role
// picker in the app top bar (screens-playful.jsx): paper container,
// ink-filled active button. Not extracted as its own primitive yet;
// if a second use case shows up (calendar view switcher, maybe?),
// pull it out then.
function SegmentedControl({ value, onChange, options }) {
  return (
    <div
      role="radiogroup"
      className="inline-flex gap-1 p-1 bg-paper border border-rule rounded-full"
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={[
              'px-3 py-1.5 rounded-full',
              'text-sm font-semibold',
              'transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
              active
                ? 'bg-ink text-paper'
                : 'bg-transparent text-ink hover:bg-bg',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}